import { Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import { vars } from '@etg/wings';
import { Icon, LoadingDots } from '@eti/components';
import { css, etiColors, resetButtonStyle, mediaQueries } from '@eti/styles';
import { CurrencyAmount } from '@eti/types';
import { price as priceUtil } from '@eti/utils';
import { useTranslation } from '@eti/providers';
import Media from 'react-media';
import { useToggle } from '../../../../common/custom-hooks/toggle';
import {
  getTripDisplayPrice,
  getTripTotalPrice,
  getTripTotalPriceWithDiscount,
  usePriceDisplayType,
} from '../../utils/resultUtils';
import Loading from '../../../../common/loading/components/Loading';

const textStyles = css`
  color: ${etiColors.grayScale[9]};
  font-size: 0.625rem;
  margin: -0.0625rem 0.125rem 0;

  @media ${mediaQueries.large.up} {
    font-size: 0.75rem;
  }
`;

const infoStyles = css`
  align-items: center;
  display: flex;
`;

const buttonStyles = css`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: fit-content;
  ${resetButtonStyle};
`;

const emphasizedPriceStyle = css`
  color: ${etiColors.black};
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: -2px;
  margin-top: -2px;

  @media ${mediaQueries.large.up} {
    font-size: 1.25rem;
    margin-bottom: -4px;
  }
`;

const priceStyles = (emphasized) => css`
  ${emphasized
    ? emphasizedPriceStyle
    : css`
        font-size: 0.6rem;
      `}
`;

const tripTypeTitleStyles = css`
  font-size: 0.75rem;
  margin-bottom: -2px;
  margin-top: -3px;
  text-align: end;

  @media ${mediaQueries.large.up} {
    margin-bottom: -4px;
  }
`;

const loadingDotsContainerStyles = css`
  color: ${vars.colors.links.default};
  display: flex;
  justify-content: center;
`;

const loadingStyles = css`
  align-self: end;
  font-size: 1.25rem;
  height: 18px;
`;

const Modal = lazy(() => import('./PriceBreakdownModal'));

const PriceContentButton = ({
  cfarTicketPrices,
  discountAmount = 0,
  flexibleTicketPrices,
  isRyanAirTicketingOrMarketingCarrierPresent,
  isSelectTripLoading = false,
  isSelectedTripRefetching = false,
  isVI = false,
  shouldDisplayBrandedFares = false,
  marketingCarrierNames,
  paymentMethodPrices,
  travelerPriceBreakdown = [],
  travelerPrices,
  tripTypeTitle,
}) => {
  const { isOpen, handleOpen, handleClose } = useToggle();
  const { t } = useTranslation();

  const { priceDisplayType, priceDisplayText } = usePriceDisplayType(discountAmount);
  const totalPrice = priceUtil.formatPrice(
    getTripDisplayPrice(priceDisplayType)(travelerPrices, flexibleTicketPrices, cfarTicketPrices),
  );
  const discountedTotalPrice = priceUtil.formatPrice(
    getTripTotalPriceWithDiscount(
      travelerPrices,
      flexibleTicketPrices,
      cfarTicketPrices,
      discountAmount,
    ),
  );

  let priceTestIdSuffix;

  if (flexibleTicketPrices) {
    priceTestIdSuffix = '-flex';
  } else if (cfarTicketPrices) {
    priceTestIdSuffix = '-cfar';
  } else {
    priceTestIdSuffix = '-standard';
  }

  if (shouldDisplayBrandedFares && (isSelectTripLoading || isSelectedTripRefetching)) {
    return (
      <Suspense fallback={<LoadingDots containerStyle={loadingDotsContainerStyles} />}>
        <Loading className={loadingStyles} />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<LoadingDots containerStyle={loadingDotsContainerStyles} />}>
      <button
        className={buttonStyles}
        data-testid="result-trip-price-info-button"
        onClick={handleOpen}
        type="button"
      >
        <Media query={{ maxWidth: 1169 }}>
          {(matches) =>
            matches && tripTypeTitle ? (
              <h3 className={tripTypeTitleStyles}>{tripTypeTitle}</h3>
            ) : null
          }
        </Media>
        {discountAmount > 0 && (
          <div className={infoStyles}>
            <span
              className={priceStyles(true)}
              data-testid={`result-trip-discounted-price${priceTestIdSuffix}`}
            >
              {discountedTotalPrice}
            </span>
          </div>
        )}
        <span
          className={priceStyles(!discountAmount > 0)}
          data-testid={`result-trip-price${priceTestIdSuffix}`}
        >
          {discountAmount > 0
            ? t('SelfServiceRebooking.VoucherDiscount.Text', totalPrice)
            : totalPrice}
        </span>
        <div className={infoStyles}>
          <span className={textStyles}>{priceDisplayText}</span>
          <Icon color={`${etiColors.grayScale[6]}`} id="info-circle" size="0.625rem" />
        </div>
      </button>
      <Modal
        cfarTicketPrices={cfarTicketPrices}
        discountAmount={discountAmount}
        flexibleTicketPrices={flexibleTicketPrices}
        handleClose={handleClose}
        isOpen={isOpen}
        isRyanAirTicketingOrMarketingCarrierPresent={isRyanAirTicketingOrMarketingCarrierPresent}
        isVI={isVI}
        marketingCarrierNames={marketingCarrierNames}
        paymentMethodPrices={paymentMethodPrices}
        totalPrice={getTripTotalPrice(travelerPrices, flexibleTicketPrices, cfarTicketPrices)}
        travelerPriceBreakdown={travelerPriceBreakdown}
      />
    </Suspense>
  );
};

export default PriceContentButton;

PriceContentButton.propTypes = {
  cfarTicketPrices: PropTypes.number,
  discountAmount: PropTypes.number,
  flexibleTicketPrices: PropTypes.arrayOf(
    PropTypes.shape({
      price: PropTypes.shape({
        price: PropTypes.shape({
          value: PropTypes.number,
        }),
      }),
    }),
  ),
  isRyanAirTicketingOrMarketingCarrierPresent: PropTypes.bool,
  isSelectedTripRefetching: PropTypes.bool,
  isSelectTripLoading: PropTypes.bool,
  isVI: PropTypes.bool,
  marketingCarrierNames: PropTypes.string,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.oneOfType([PropTypes.number, PropTypes.shape({ value: PropTypes.number })]),
    }),
  ),
  shouldDisplayBrandedFares: PropTypes.bool,
  travelerPriceBreakdown: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      markup: PropTypes.number,
      price: PropTypes.number,
      quantity: PropTypes.number,
      taxesAndFees: PropTypes.arrayOf(
        PropTypes.shape({
          category: PropTypes.string,
          taxes: PropTypes.arrayOf(
            PropTypes.shape({
              amount: CurrencyAmount,
              code: PropTypes.string,
              title: PropTypes.string,
            }),
          ),
        }),
      ),
      totalPrice: PropTypes.number,
      totalPriceSum: PropTypes.number,
      vat: PropTypes.number,
    }),
  ),
  travelerPrices: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      markup: PropTypes.number,
      price: PropTypes.number,
      quantity: PropTypes.number,
      totalPrice: PropTypes.number,
      totalPriceSum: PropTypes.number,
      vat: PropTypes.number,
    }),
  ),
  tripTypeTitle: PropTypes.string,
};
